import { useState, useEffect } from 'react'
import { X, Eye, EyeOff } from 'lucide-react'
import { Employee, WorkCenter, Position, ShiftModel, ContractType, CONTRACT_TYPE_LABELS, ShiftType, ShiftLetter, SHIFT_TYPE_LABELS, SHIFT_LETTER_LABELS, VALID_SHIFTS_BY_TYPE } from '../../types'
import { workCenterService } from '../../services/workCenterService'
import { positionService } from '../../services/positionService'
import { shiftService } from '../../services/shiftService'

interface EmployeeFormData {
  employeeId: string
  dni: string
  firstName: string
  lastName: string
  email: string
  positionId: string
  shiftId: string  // Legacy - reference to Shift table
  shiftType: ShiftType  // Tipo de relevo: R5, R3, R2
  shiftLetter: ShiftLetter  // Letra del turno: A, B, C, D, E
  workCenterId: string
  hasStop: boolean
  daysAt5Shifts: number
  startDate: string
  isPresecadoCelulosa: boolean
  exemptFromLimits: boolean
  contractType: ContractType
  isCloseToRetirement: boolean
  partialRetirementDate: string
  birthDate: string
  hadExcedenciaCorta2Years: boolean
  hadExcedenciaEspecial2Years: boolean
  daysUsedPP5R: number
  canRequestPP5R: boolean
  arrancadaDates: string
  password: string
  isActive?: boolean
  userIsActive?: boolean
}

interface EmployeeFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: EmployeeFormData) => Promise<void>
  employee?: Employee | null
  isLoading?: boolean
}

export function EmployeeFormModal({
  isOpen,
  onClose,
  onSubmit,
  employee,
  isLoading,
}: EmployeeFormModalProps) {
  const isEditing = !!employee
  const [showPassword, setShowPassword] = useState(false)
  const [workCenters, setWorkCenters] = useState<WorkCenter[]>([])
  const [positions, setPositions] = useState<Position[]>([])
  const [shifts, setShifts] = useState<ShiftModel[]>([])
  const [loadingCenters, setLoadingCenters] = useState(true)
  const [loadingPositions, setLoadingPositions] = useState(true)
  const [formData, setFormData] = useState<EmployeeFormData>({
    employeeId: '',
    dni: '',
    firstName: '',
    lastName: '',
    email: '',
    positionId: '',
    shiftId: '',
    shiftType: 'R5',  // Default a 5 Relevos
    shiftLetter: 'A',  // Default a Turno A
    workCenterId: '',
    hasStop: false,
    daysAt5Shifts: 0,
    startDate: new Date().toISOString().split('T')[0],
    isPresecadoCelulosa: false,
    exemptFromLimits: false,
    contractType: 'INDEFINIDO',
    isCloseToRetirement: false,
    partialRetirementDate: '',
    birthDate: '',
    hadExcedenciaCorta2Years: false,
    hadExcedenciaEspecial2Years: false,
    daysUsedPP5R: 0,
    canRequestPP5R: false,
    arrancadaDates: '',
    password: '',
    isActive: true,
    userIsActive: true,
  })

  // Cargar centros de trabajo, puestos y turnos activos
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingCenters(true)
        setLoadingPositions(true)

        const [centers, pos, sh] = await Promise.all([
          workCenterService.getActive(),
          positionService.getAll(),
          shiftService.getActive(),
        ])

        setWorkCenters(centers)
        setPositions(pos)
        setShifts(sh)

        // Si no hay workCenterId y hay centros, seleccionar el primero
        if (!formData.workCenterId && centers.length > 0) {
          setFormData(prev => ({ ...prev, workCenterId: centers[0].id }))
        }
        // Si no hay positionId y hay puestos, seleccionar el primero
        if (!formData.positionId && pos.length > 0) {
          setFormData(prev => ({ ...prev, positionId: pos[0].id }))
        }
      } catch (error) {
        console.error('Error loading data:', error)
      } finally {
        setLoadingCenters(false)
        setLoadingPositions(false)
      }
    }

    if (isOpen) {
      loadData()
    }
  }, [isOpen])

  useEffect(() => {
    if (employee) {
      setFormData({
        employeeId: employee.employeeId,
        dni: employee.dni,
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.email || '',
        positionId: employee.positionId || '',
        shiftId: employee.shiftId || '',
        shiftType: employee.shiftType || 'R5',
        shiftLetter: employee.shiftLetter || 'A',
        workCenterId: employee.workCenterId || '',
        hasStop: employee.hasStop,
        daysAt5Shifts: employee.daysAt5Shifts,
        startDate: employee.startDate.split('T')[0],
        isPresecadoCelulosa: employee.isPresecadoCelulosa,
        exemptFromLimits: employee.exemptFromLimits,
        contractType: employee.contractType,
        isCloseToRetirement: employee.isCloseToRetirement || false,
        partialRetirementDate: employee.partialRetirementDate ? employee.partialRetirementDate.split('T')[0] : '',
        birthDate: employee.birthDate ? employee.birthDate.split('T')[0] : '',
        hadExcedenciaCorta2Years: employee.hadExcedenciaCorta2Years || false,
        hadExcedenciaEspecial2Years: employee.hadExcedenciaEspecial2Years || false,
        daysUsedPP5R: employee.daysUsedPP5R || 0,
        canRequestPP5R: employee.canRequestPP5R || false,
        arrancadaDates: employee.arrancadaDates || '',
        password: '',
        isActive: employee.isActive,
        userIsActive: true,
      })
    } else {
      setFormData({
        employeeId: '',
        dni: '',
        firstName: '',
        lastName: '',
        email: '',
        positionId: positions.length > 0 ? positions[0].id : '',
        shiftId: shifts.length > 0 ? shifts[0].id : '',
        shiftType: 'R5',
        shiftLetter: 'A',
        workCenterId: workCenters.length > 0 ? workCenters[0].id : '',
        hasStop: false,
        daysAt5Shifts: 0,
        startDate: new Date().toISOString().split('T')[0],
        isPresecadoCelulosa: false,
        exemptFromLimits: false,
        contractType: 'INDEFINIDO',
        isCloseToRetirement: false,
        partialRetirementDate: '',
        birthDate: '',
        hadExcedenciaCorta2Years: false,
        hadExcedenciaEspecial2Years: false,
        daysUsedPP5R: 0,
        canRequestPP5R: false,
        arrancadaDates: '',
        password: '',
        isActive: true,
        userIsActive: true,
      })
    }
  }, [employee, workCenters, positions, shifts])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(formData)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white dark:bg-slate-800 sm:rounded-xl shadow-xl max-w-2xl w-full min-h-screen sm:min-h-0 sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
            {isEditing ? 'Editar Empleado' : 'Nuevo Empleado'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors text-gray-500 dark:text-gray-400"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-3 sm:p-4 space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {/* ID Empleado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                ID Empleado *
              </label>
              <input
                type="text"
                className="input"
                value={formData.employeeId}
                onChange={(e) =>
                  setFormData({ ...formData, employeeId: e.target.value })
                }
                required
                disabled={isEditing}
              />
            </div>

            {/* DNI */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                DNI * <span className="text-xs text-gray-400">(para login)</span>
              </label>
              <input
                type="text"
                className="input"
                value={formData.dni}
                onChange={(e) =>
                  setFormData({ ...formData, dni: e.target.value.toUpperCase() })
                }
                required
                placeholder="12345678A"
                disabled={isEditing}
              />
            </div>

            {/* Nombre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nombre *
              </label>
              <input
                type="text"
                className="input"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                required
              />
            </div>

            {/* Apellidos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Apellidos *
              </label>
              <input
                type="text"
                className="input"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                required
              />
            </div>

            {/* Puesto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Puesto *
              </label>
              <select
                className="input"
                value={formData.positionId}
                onChange={(e) =>
                  setFormData({ ...formData, positionId: e.target.value })
                }
                required
                disabled={loadingPositions}
              >
                {loadingPositions ? (
                  <option value="">Cargando...</option>
                ) : positions.length === 0 ? (
                  <option value="">No hay puestos disponibles</option>
                ) : (
                  positions.map((pos) => (
                    <option key={pos.id} value={pos.id}>
                      {pos.name}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Centro de Trabajo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Centro de Trabajo *
              </label>
              <select
                className="input"
                value={formData.workCenterId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    workCenterId: e.target.value,
                  })
                }
                required
                disabled={loadingCenters}
              >
                {loadingCenters ? (
                  <option value="">Cargando...</option>
                ) : workCenters.length === 0 ? (
                  <option value="">No hay centros disponibles</option>
                ) : (
                  workCenters.map((wc) => (
                    <option key={wc.id} value={wc.id}>
                      {wc.name}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* Tipo de Relevo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tipo de Relevo *
              </label>
              <select
                className="input"
                value={formData.shiftType}
                onChange={(e) => {
                  const newShiftType = e.target.value as ShiftType
                  const validShifts = VALID_SHIFTS_BY_TYPE[newShiftType]
                  // Si el turno actual no es válido para el nuevo tipo, resetear al primero válido
                  const newShiftLetter = validShifts.includes(formData.shiftLetter)
                    ? formData.shiftLetter
                    : validShifts[0]
                  setFormData({
                    ...formData,
                    shiftType: newShiftType,
                    shiftLetter: newShiftLetter
                  })
                }}
                required
              >
                {Object.entries(SHIFT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Turno (Letra) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Turno *
              </label>
              <select
                className="input"
                value={formData.shiftLetter}
                onChange={(e) =>
                  setFormData({ ...formData, shiftLetter: e.target.value as ShiftLetter })
                }
                required
              >
                {VALID_SHIFTS_BY_TYPE[formData.shiftType].map((letter) => (
                  <option key={letter} value={letter}>
                    {SHIFT_LETTER_LABELS[letter]}
                  </option>
                ))}
              </select>
            </div>

            {/* Fecha de Alta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fecha de Alta *
              </label>
              <input
                type="date"
                className="input"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
                required
              />
            </div>

            {/* Dias a 5 Relevos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Dias a 5 Relevos
              </label>
              <input
                type="number"
                className="input"
                min="0"
                value={formData.daysAt5Shifts}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    daysAt5Shifts: parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>

            {/* Tipo de Contrato */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tipo de Contrato *
              </label>
              <select
                className="input"
                value={formData.contractType}
                onChange={(e) =>
                  setFormData({ ...formData, contractType: e.target.value as ContractType })
                }
                required
              >
                {Object.entries(CONTRACT_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Email (opcional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email <span className="text-xs text-gray-400">(opcional)</span>
              </label>
              <input
                type="email"
                className="input"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="correo@ejemplo.com"
              />
            </div>

            {/* Fecha de Nacimiento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fecha de Nacimiento
              </label>
              <input
                type="date"
                className="input"
                value={formData.birthDate}
                onChange={(e) =>
                  setFormData({ ...formData, birthDate: e.target.value })
                }
              />
            </div>

            {/* Fecha Jubilación Parcial */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Fecha Jubilación Parcial
              </label>
              <input
                type="date"
                className="input"
                value={formData.partialRetirementDate}
                onChange={(e) =>
                  setFormData({ ...formData, partialRetirementDate: e.target.value })
                }
              />
            </div>

            {/* Días usados PP5R */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Días usados PP5R
              </label>
              <input
                type="number"
                className="input"
                min="0"
                value={formData.daysUsedPP5R}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    daysUsedPP5R: parseInt(e.target.value) || 0,
                  })
                }
              />
            </div>

            {/* Password */}
            {isEditing && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nueva Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input pr-10"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    placeholder="Dejar vacío para no cambiar"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {!isEditing && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                <strong>Nota:</strong> El empleado recibirá la contraseña genérica <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">Viscofan.2026</code> y deberá cambiarla en su primer acceso.
              </p>
            </div>
          )}

          {/* Checkboxes */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3 sm:gap-4 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 h-4 w-4"
                checked={formData.hasStop}
                onChange={(e) =>
                  setFormData({ ...formData, hasStop: e.target.checked })
                }
              />
              <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Tiene parada</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 h-4 w-4"
                checked={formData.isPresecadoCelulosa}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    isPresecadoCelulosa: e.target.checked,
                  })
                }
              />
              <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Presecado Celulosa</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer" title="Laboratorio, Mantenimiento, Cogeneración: no computan en límites máximos de LSS">
              <input
                type="checkbox"
                className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 h-4 w-4"
                checked={formData.exemptFromLimits}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    exemptFromLimits: e.target.checked,
                  })
                }
              />
              <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Exento de límites</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer" title="¿Puede solicitar Premio Permanencia 5R? (dato proporcionado por el cliente)">
              <input
                type="checkbox"
                className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 h-4 w-4"
                checked={formData.canRequestPP5R}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    canRequestPP5R: e.target.checked,
                  })
                }
              />
              <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Puede PP5R</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer" title="Para Premio Permanencia 5R: si tiene menos de 25 años de antigüedad pero está próximo a jubilación">
              <input
                type="checkbox"
                className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 h-4 w-4"
                checked={formData.isCloseToRetirement}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    isCloseToRetirement: e.target.checked,
                  })
                }
              />
              <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Próximo a jubilación</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer" title="Si ha disfrutado Excedencia Corta en los últimos 2 años">
              <input
                type="checkbox"
                className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 h-4 w-4"
                checked={formData.hadExcedenciaCorta2Years}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    hadExcedenciaCorta2Years: e.target.checked,
                  })
                }
              />
              <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Exc. Corta 2 años</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer" title="Si ha disfrutado Excedencia Voluntaria Especial en los últimos 2 años">
              <input
                type="checkbox"
                className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 h-4 w-4"
                checked={formData.hadExcedenciaEspecial2Years}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    hadExcedenciaEspecial2Years: e.target.checked,
                  })
                }
              />
              <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Exc. Especial 2 años</span>
            </label>

            {isEditing && (
              <>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 h-4 w-4"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                  <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Empleado activo</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 h-4 w-4"
                    checked={formData.userIsActive}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        userIsActive: e.target.checked,
                      })
                    }
                  />
                  <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">Usuario activo</span>
                </label>
              </>
            )}
          </div>

          {/* Buttons */}
          <div className="flex flex-col-reverse xs:flex-row justify-end gap-2 pt-4 border-t border-gray-200 dark:border-slate-700 pb-safe">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary py-2.5 sm:py-2 text-sm"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary py-2.5 sm:py-2 text-sm"
              disabled={isLoading}
            >
              {isLoading ? 'Guardando...' : isEditing ? 'Guardar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
